import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { InsentifKegiatan, PegawaiListItem, PeriodeHonor } from '@app/shared';
import { AdminLayout } from '../../components/admin-layout';
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Pagination,
  Select,
  Table,
  Td,
  Th,
} from '../../components/ui';
import { getPeriodeHonorList } from '../../features/honorarium/api';
import { getPegawaiList } from '../../features/master/api';
import {
  createInsentifKegiatan,
  deleteInsentifKegiatan,
  getInsentifKegiatanList,
  updateInsentifKegiatan,
} from '../../features/settings/api';

type FormState = {
  pegawai_sekolah_id: string;
  periode_id: string;
  nama_kegiatan: string;
  tanggal_kegiatan: string;
  nominal: string;
  keterangan: string;
};

const emptyForm = (): FormState => ({
  pegawai_sekolah_id: '',
  periode_id: '',
  nama_kegiatan: '',
  tanggal_kegiatan: new Date().toISOString().split('T')[0] ?? '',
  nominal: '',
  keterangan: '',
});

export function InsentifKegiatanPage() {
  const [items, setItems] = useState<InsentifKegiatan[]>([]);
  const [pegawaiList, setPegawaiList] = useState<PegawaiListItem[]>([]);
  const [periodeList, setPeriodeList] = useState<PeriodeHonor[]>([]);
  const [meta, setMeta] = useState({ page: 1, total_pages: 1 });
  const [search, setSearch] = useState('');
  const [filterPeriode, setFilterPeriode] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<InsentifKegiatan | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const [insentifResult, pegawaiResult, periodeResult] = await Promise.all([
          getInsentifKegiatanList({
            page,
            limit: 10,
            sort_dir: 'desc',
            search: search || undefined,
            periode_id: filterPeriode ? Number(filterPeriode) : undefined,
          }),
          getPegawaiList({ limit: 200, status_aktif: true }),
          getPeriodeHonorList({ limit: 50 }),
        ]);
        setItems(insentifResult.rows);
        setMeta({ page: insentifResult.meta.page, total_pages: insentifResult.meta.total_pages });
        setPegawaiList(pegawaiResult.rows);
        setPeriodeList(periodeResult.rows);
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Gagal memuat insentif kegiatan');
      } finally {
        setLoading(false);
      }
    },
    [search, filterPeriode],
  );

  useEffect(() => {
    void load(1);
  }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEdit = (item: InsentifKegiatan) => {
    setEditing(item);
    setForm({
      pegawai_sekolah_id: item.pegawai_sekolah_id ? String(item.pegawai_sekolah_id) : '',
      periode_id: item.periode_id ? String(item.periode_id) : '',
      nama_kegiatan: item.nama_kegiatan,
      tanggal_kegiatan: item.tanggal_kegiatan || new Date().toISOString().split('T')[0]!,
      nominal: String(item.nominal),
      keterangan: item.keterangan || '',
    });
    setModalOpen(true);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);

    const nominal = Number(form.nominal);
    if (!Number.isInteger(nominal) || nominal < 0) {
      setError('Nominal harus berupa angka non-negatif');
      setSaving(false);
      return;
    }

    try {
      if (editing) {
        await updateInsentifKegiatan(editing.id, {
          nama_kegiatan: form.nama_kegiatan.trim(),
          tanggal_kegiatan: form.tanggal_kegiatan,
          nominal,
          keterangan: form.keterangan.trim() || undefined,
        });
        setMessage('Insentif diperbarui.');
      } else {
        if (!form.pegawai_sekolah_id || !form.periode_id) {
          setError('Pegawai dan periode wajib dipilih');
          setSaving(false);
          return;
        }
        await createInsentifKegiatan({
          pegawai_sekolah_id: Number(form.pegawai_sekolah_id),
          periode_id: Number(form.periode_id),
          nama_kegiatan: form.nama_kegiatan.trim(),
          tanggal_kegiatan: form.tanggal_kegiatan,
          nominal,
          keterangan: form.keterangan.trim() || undefined,
        });
        setMessage('Insentif ditambahkan.');
      }
      setModalOpen(false);
      await load(meta.page);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menyimpan insentif');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    if (!confirm('Hapus insentif ini?')) return;
    try {
      await deleteInsentifKegiatan(id);
      setMessage('Insentif dihapus.');
      await load(meta.page);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menghapus insentif');
    }
  };

  return (
    <AdminLayout title="Insentif Kegiatan">
      {error && <Alert type="error">{error}</Alert>}
      {message && <Alert type="success">{message}</Alert>}
      <Card>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <Input
            placeholder="Cari nama pegawai/kegiatan..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '200px' }}
          />
          <Select
            value={filterPeriode}
            onChange={(e) => setFilterPeriode(e.target.value)}
            style={{ minWidth: '160px' }}
          >
            <option value="">Semua Periode</option>
            {periodeList.map((p) => (
              <option key={p.id} value={p.id}>
                {p.kode_periode}
              </option>
            ))}
          </Select>
          <Button onClick={openAdd}>Tambah Insentif</Button>
        </div>
        {loading ? (
          <p>Memuat…</p>
        ) : items.length === 0 ? (
          <p style={{ color: '#64748B' }}>Belum ada insentif. Tambahkan item pertama.</p>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Pegawai</Th>
                  <Th>Sekolah</Th>
                  <Th>Periode</Th>
                  <Th>Tanggal</Th>
                  <Th>Nama Kegiatan</Th>
                  <Th style={{ textAlign: 'right' }}>Nominal (Rp)</Th>
                  <Th style={{ width: 140 }}>Aksi</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <Td>{item.nama_pegawai || '-'}</Td>
                    <Td>{item.nama_sekolah || '-'}</Td>
                    <Td>{item.kode_periode || '-'}</Td>
                    <Td>{item.tanggal_kegiatan}</Td>
                    <Td>{item.nama_kegiatan}</Td>
                    <Td style={{ textAlign: 'right' }}>{item.nominal.toLocaleString('id-ID')}</Td>
                    <Td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <Button variant="secondary" onClick={() => openEdit(item)}>
                          Ubah
                        </Button>
                        <Button variant="danger" onClick={() => void remove(item.id)}>
                          Hapus
                        </Button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              page={meta.page}
              totalPages={meta.total_pages}
              onChange={(page) => void load(page)}
            />
          </>
        )}
      </Card>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Ubah Insentif' : 'Tambah Insentif'}
      >
        <form onSubmit={submit}>
          <Select
            label="Pegawai"
            value={form.pegawai_sekolah_id}
            disabled={Boolean(editing)}
            onChange={(e) => setForm({ ...form, pegawai_sekolah_id: e.target.value })}
            required
          >
            <option value="">Pilih pegawai</option>
            {pegawaiList.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nip_nik} — {p.nama}
              </option>
            ))}
          </Select>
          <Select
            label="Periode"
            value={form.periode_id}
            disabled={Boolean(editing)}
            onChange={(e) => setForm({ ...form, periode_id: e.target.value })}
            required
          >
            <option value="">Pilih periode</option>
            {periodeList.map((p) => (
              <option key={p.id} value={p.id}>
                {p.kode_periode} — {p.nama}
              </option>
            ))}
          </Select>
          <Input
            label="Nama Kegiatan"
            name="nama_kegiatan"
            value={form.nama_kegiatan}
            onChange={(e) => setForm({ ...form, nama_kegiatan: e.target.value })}
            required
            minLength={1}
            maxLength={255}
          />
          <Input
            label="Tanggal Kegiatan"
            name="tanggal_kegiatan"
            type="date"
            value={form.tanggal_kegiatan}
            onChange={(e) => setForm({ ...form, tanggal_kegiatan: e.target.value })}
            required
          />
          <Input
            label="Nominal (Rp)"
            name="nominal"
            type="number"
            min={0}
            value={form.nominal}
            onChange={(e) => setForm({ ...form, nominal: e.target.value })}
            required
          />
          <Input
            label="Keterangan (Opsional)"
            name="keterangan"
            value={form.keterangan}
            onChange={(e) => setForm({ ...form, keterangan: e.target.value })}
            maxLength={500}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.5rem',
              marginTop: '1rem',
            }}
          >
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>
              Batal
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Menyimpan…' : 'Simpan'}
            </Button>
          </div>
        </form>
      </Modal>
    </AdminLayout>
  );
}
